import {
  PostIndi,
  useCreateFamilyTree,
  buildDataCollectionRecordsLookup,
  useGetDataCollectionRecords,
  COLLECTION_TYPE_TO_NAME_MAP,
  useFocusedFamilyTreeMember,
} from '@ltvco/refresh-lib/fmt';
import {
  DataCollectionRecord,
  COLLECTION_RECORD_IDS,
  CollectionRecordType,
} from '@ltvco/refresh-lib/hooks';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { CollectionRecordCard } from '@ltvco/refresh-lib/v2';
import React, { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSearchPersonResultsContext } from '../context';

import { MasonryCardsContainer } from './MasonryCardsContainer';
import { MobileIndexCollectionCard } from './NewCardsDesignVariation1/IndexCollectionCards/MobileIndexCollectionCard';

interface CollectionRecordCardsProps {
  records: DataCollectionRecord[];
  tabName?: string;
}

export const CollectionRecordCards = ({
  records,
  tabName,
}: CollectionRecordCardsProps) => {
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const { meetMobileCardInDesktopCriteria } = useSearchPersonResultsContext();
  const { createFamilyTree, isCreatingFamilyTree } = useCreateFamilyTree();
  const { navigateToFocusedMember } = useFocusedFamilyTreeMember();
  const {
    data: dataCollectionRecords,
    isLoading: isDataCollectionRecordsLoading,
    error: errorDataCollectionRecords,
  } = useGetDataCollectionRecords({
    bodyOptions: {
      placeholders_excluded: true,
    },
    logNotFoundErrorEnabled: false,
  });

  const noTreeCreated = errorDataCollectionRecords?.message?.includes('404');

  const lookup = buildDataCollectionRecordsLookup(
    dataCollectionRecords?.records || []
  );

  const handleAddToFamilyTree = async (
    postIndi: PostIndi,
    recordInTree: boolean,
    recordId: string,
    collectionId: string
  ) => {
    if (isDataCollectionRecordsLoading) return;
    // If record is already in tree, navigate to existing member
    if (recordInTree) {
      // Use the lookup Map to get the record directly for O(1) access
      const key = `${recordId}-${collectionId}`;
      const record = lookup.get(key);
      const memberId = record?.member_id;
      navigateToFocusedMember(memberId as number);
      return;
    }

    if (noTreeCreated) {
      await createFamilyTree({
        indi: postIndi,
      });
      return;
    }

    localStorage.setItem('familyTreeRelativeToAdd', JSON.stringify(postIndi));

    navigate('/family-tree');
  };

  const Container = meetMobileCardInDesktopCriteria
    ? MasonryCardsContainer
    : Fragment;

  return (
    <Container>
      {records.map((record: DataCollectionRecord, index: number) => {
        const collectionName =
          COLLECTION_TYPE_TO_NAME_MAP[record.collectionRecordType] ??
          'Collection Record';
        const recordType = record.collectionRecordType;
        const collectionId: CollectionRecordType = COLLECTION_RECORD_IDS[
          recordType
        ] as CollectionRecordType;
        const recordInTree = lookup.has(`${record.recordId}-${collectionId}`);

        if (isMobile || meetMobileCardInDesktopCriteria) {
          return (
            <React.Fragment key={index}>
              <MobileIndexCollectionCard
                indexCollectionRecord={record}
                collectionName={collectionName}
                collectionId={collectionId}
                handleAddToFamilyTree={(postIndi: PostIndi) =>
                  handleAddToFamilyTree(
                    postIndi,
                    recordInTree,
                    String(record.recordId),
                    collectionId
                  )
                }
                recordInTree={recordInTree}
                tabName={tabName}
              />
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={index}>
            <CollectionRecordCard
              collectionRecord={record}
              collectionName={collectionName}
              collectionId={collectionId}
              isCreatingFamilyTree={isCreatingFamilyTree}
              handleAddToFamilyTree={(postIndi: PostIndi) =>
                handleAddToFamilyTree(
                  postIndi,
                  recordInTree,
                  String(record.recordId),
                  collectionId
                )
              }
              recordInTree={recordInTree}
              tabName={tabName}
            />
          </React.Fragment>
        );
      })}
    </Container>
  );
};
